import { FormEvent, useState } from "react";
import moment from "moment";

import instance from "../../../api/instance";
import { useSyncGlobalPageLoader } from "../../../contexts/global-page-loader-context";

type GameType = "all" | "match" | "fancy";

interface GameOption {
  label: string;
  value: string;
}

interface ReportRow {
  sr_no: number;
  name: string;
  amount: number | string;
}

const GameReportPage = () => {
  const [fromDate, setFromDate] = useState(moment().subtract(7, "days").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [gameType, setGameType] = useState<GameType>("all");

  const [gameOptions, setGameOptions] = useState<GameOption[]>([]);
  const [selectedGame, setSelectedGame] = useState("all");

  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [gameListLoading, setGameListLoading] = useState(false);

  useSyncGlobalPageLoader(loading || gameListLoading);

  const handleGameList = async (e: FormEvent) => {
    e.preventDefault();
    setGameListLoading(true);
    try {
      const { data } = await instance.get("game-report/game-list", {
        params: {
          from: moment(fromDate).unix(),
          to: moment(toDate).unix(),
          type: gameType,
        },
      });
      const list = Array.isArray(data?.data) ? data.data : [];
      const options: GameOption[] = [
        { label: "All", value: "all" },
        ...list.map((g: any) => ({ label: g.name ?? g.event_name, value: g.id ?? g.event_id })),
      ];
      setGameOptions(options);
      setSelectedGame("all");
      setRows([]);
    } catch {
      setGameOptions([{ label: "All", value: "all" }]);
    } finally {
      setGameListLoading(false);
    }
  };

  const handleShowReport = async (reportType: "game" | "master") => {
    setLoading(true);
    try {
      const endpoint = reportType === "master" ? "game-report/master" : "game-report";
      const { data } = await instance.get(endpoint, {
        params: {
          from: moment(fromDate).unix(),
          to: moment(toDate).unix(),
          type: gameType,
          game: selectedGame,
        },
      });
      const list: ReportRow[] = Array.isArray(data?.data) ? data.data : [];
      setRows(list);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="listing-grid report-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Game Report</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      {/* Top form: From / To / Type / Game List */}
      <div className="row mt-3">
        <div className="col-12">
          <form method="post" onSubmit={handleGameList}>
            <div className="row">
              <div className="col-md-2">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>From</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label>To</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              <div className="col-md-4 col-xl-2">
                <div className="form-group">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label>Type</label>
                  <select
                    name="type"
                    className="form-control"
                    value={gameType}
                    onChange={(e) => setGameType(e.target.value as GameType)}
                  >
                    <option value="all">All</option>
                    <option value="match">Match</option>
                    <option value="fancy">Fancy</option>
                  </select>
                </div>
              </div>
              <div className="col-md-4 col-xl-3 mt-4">
                <button type="submit" className="btn btn-primary" disabled={gameListLoading}>
                  {gameListLoading ? "Loading..." : "Game List"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Bottom form: Game select / Show Game Report / Master Game Report */}
      <div className="row">
        <div className="col-12">
          <form
            method="post"
            onSubmit={(e) => {
              e.preventDefault();
              handleShowReport("game");
            }}
          >
            <div className="row">
              <div className="col-md-8 col-xl-6">
                <div className="form-group">
                  <select
                    name="type"
                    className="form-control"
                    value={selectedGame}
                    onChange={(e) => setSelectedGame(e.target.value)}
                  >
                    {gameOptions.length === 0 ? (
                      <option value="all">All</option>
                    ) : (
                      gameOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>
              <div className="col-md-4 col-xl-3">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  Show Game Report
                </button>{" "}
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={loading}
                  onClick={() => handleShowReport("master")}
                >
                  Master Game Report
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive mb-0 report-table">
        <table
          role="table"
          aria-busy="false"
          aria-colcount={3}
          className="table b-table table-striped table-bordered"
        >
          <thead>
            <tr role="row">
              <th role="columnheader" scope="col" aria-colindex={1}>
                <div>Sr.No</div>
              </th>
              <th role="columnheader" scope="col" aria-colindex={2}>
                <div>Name</div>
              </th>
              <th role="columnheader" scope="col" aria-colindex={3}>
                <div>Amount</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr className="b-table-empty-row">
                <td colSpan={3}>
                  <div role="alert" aria-live="polite">
                    <p className="text-center mb-0">
                      {loading ? "Loading..." : "No data available in table"}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={idx} role="row">
                  <td aria-colindex={1}>{row.sr_no ?? idx + 1}</td>
                  <td aria-colindex={2}>{row.name}</td>
                  <td aria-colindex={3}>{row.amount}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GameReportPage;
